// Pure authority module for Groups moderation.
//
// No imports from next/* or Supabase — these are total, side-effect-free
// functions so they can be unit-tested in isolation. Every function is
// defensive: any value that isn't a known role contributes zero authority.

export type GroupRole = 'owner' | 'admin' | 'moderator' | 'member';

const RANK: Record<GroupRole, number> = {
  owner: 4,
  admin: 3,
  moderator: 2,
  member: 1,
};

/**
 * Numeric rank for a role. null / unknown strings => 0 (no authority).
 */
export function rankOf(role: GroupRole | null): number {
  if (role == null) return 0;
  const r = (RANK as Record<string, number>)[role];
  return typeof r === 'number' ? r : 0;
}

/**
 * Resolve the effective role of an actor on a group.
 * The creator is always 'owner' regardless of any membership row; everyone
 * else gets their membership role (or null when they have no membership).
 */
export function resolveActorRole(
  createdBy: string,
  actorId: string,
  membershipRole: GroupRole | null,
): GroupRole | null {
  if (actorId && createdBy && actorId === createdBy) return 'owner';
  return membershipRole ?? null;
}

/**
 * Can the actor take a moderation action (kick / ban / mute / hide a message
 * authored by someone of the target's rank) against the target?
 * Actor must be at least a moderator, the target must be a real participant,
 * and the actor must strictly outrank the target.
 */
export function canModerateTarget(
  actorRole: GroupRole | null,
  targetRole: GroupRole | null,
): boolean {
  const actor = rankOf(actorRole);
  const target = rankOf(targetRole);
  return actor >= RANK.moderator && target > 0 && target < actor;
}

/**
 * Can the actor set the target's role to newRole?
 * - Actor must be at least an admin.
 * - newRole may only be admin | moderator | member (NEVER owner).
 * - Actor must strictly outrank both the target's current role and the new role.
 * - Granting OR revoking 'admin' is owner-only.
 */
export function canAssignRole(
  actorRole: GroupRole | null,
  targetCurrentRole: GroupRole | null,
  newRole: GroupRole,
): boolean {
  const actor = rankOf(actorRole);
  if (actor < RANK.admin) return false;

  if (newRole !== 'admin' && newRole !== 'moderator' && newRole !== 'member') {
    return false;
  }

  const target = rankOf(targetCurrentRole);
  const next = rankOf(newRole);
  if (!(target < actor && next < actor)) return false;

  if (newRole === 'admin' || targetCurrentRole === 'admin') {
    return actorRole === 'owner';
  }

  return true;
}

/**
 * Can the actor hide/unhide a message authored by someone of authorRole?
 * Moderator+ only. An author who has since left the group (rank 0) outranks
 * no one, so their messages remain moderatable.
 */
export function canHideMessage(
  actorRole: GroupRole | null,
  authorRole: GroupRole | null,
): boolean {
  if (rankOf(actorRole) < RANK.moderator) return false;
  if (rankOf(authorRole) === 0) return true;
  return rankOf(authorRole) < rankOf(actorRole);
}

/**
 * Can the actor ban the target?
 * - Current member (rank > 0): strict — same rule as any moderation action.
 * - Non-member (rank 0, preemptive or re-ban): admin+ only.
 */
export function canBan(
  actorRole: GroupRole | null,
  targetRole: GroupRole | null,
): boolean {
  if (rankOf(targetRole) > 0) return canModerateTarget(actorRole, targetRole);
  return rankOf(actorRole) >= RANK.admin;
}
